import { apiClientBff } from './apiBff';
import { apiClient } from './api'; // Fallback for temp-access until migrated

interface ListObjectsResponse {
  success: boolean;
  data: {
    objects: Array<{
      Key: string;
      LastModified?: string;
      Size?: number;
      StorageClass?: string;
      downloadUrl?: string;
    }>;
  };
}

export interface UploadBiometricParams {
  farmId: string;
  animalTagOrId: string;
  file: File | Blob;
  contentType?: string; // defaults to file.type or 'image/jpeg'
  bucket?: string; // defaults to 'biometria'
}

export interface UploadBiometricResult {
  objectKey: string;
  downloadUrl: string;
}

export interface TempAccessRequest {
  expiresInMinutes?: number;
  maxAccess?: number;
  description?: string;
}

export interface TempAccessResponse {
  success: boolean;
  token: string;
  accessUrl: string;
  expiresInMinutes: number;
  maxAccess: number;
  allowedIPs?: string[];
  description?: string;
}

function extensionFromContentType(ct?: string): string {
  if (!ct) return 'jpeg';
  if (ct.includes('png')) return 'png';
  if (ct.includes('webp')) return 'webp';
  return 'jpeg';
}

function normalizeContentType(ct?: string): string {
  if (!ct) return 'image.jpeg';
  if (ct.includes('image/jpeg') || ct.includes('image/jpg')) return 'image.jpeg';
  if (ct.includes('image/png')) return 'image.png';
  if (ct.includes('image/webp')) return 'image.webp';
  return ct;
}

export const storageService = {
  async uploadBiometricImage(params: UploadBiometricParams): Promise<UploadBiometricResult> {
    const envBucket = (import.meta as any)?.env?.VITE_S3_BUCKET_BIOMETRIA;
    const bucket = params.bucket || envBucket || 'biometria';
    if (!envBucket && !params.bucket) {
      console.warn('VITE_S3_BUCKET_BIOMETRIA não definido; usando fallback "biometria"');
    }
    const rawContentType = params.contentType || (params.file as any)?.type || 'image/jpeg';
    const contentType = normalizeContentType(rawContentType);
    const ext = extensionFromContentType(contentType);
    const timestamp = Date.now();
    const objectKey = `${params.farmId}/${params.animalTagOrId}-${timestamp}.${ext}`;

    const form = new FormData();
    form.append('objectKey', objectKey);
    form.append('contentType', contentType);
    form.append('bucket', bucket);
    // Provide a filename hint for S3
    form.append('file', params.file, `${params.animalTagOrId}-${timestamp}.${ext}`);

    // POST upload via BFF
    await apiClientBff.postFormData(`/storage/upload`, form);

    // GET list and extract the downloadUrl for our objectKey (with simple retry for eventual consistency)
    const tryFetchUrl = async (): Promise<string | null> => {
      // Usar a nova rota de objetos do BFF se disponível, ou ajustar conforme a API do BFF
      const listResp = await apiClientBff.get<ListObjectsResponse>(`/storage/objects?bucket=${bucket}`);
      const objects = listResp?.data?.objects || [];
      const match = objects.find(o => o.Key === objectKey);
      return match?.downloadUrl || null;
    };

    let downloadUrl: string | null = await tryFetchUrl();
    if (!downloadUrl) {
      await new Promise(res => setTimeout(res, 700));
      downloadUrl = await tryFetchUrl();
    }
    if (!downloadUrl) {
      await new Promise(res => setTimeout(res, 1200));
      downloadUrl = await tryFetchUrl();
    }
    if (!downloadUrl) {
      // Fallback para uma URL construída manualmente se não conseguirmos recuperar a URL assinada
      console.warn('URL do arquivo não encontrada após upload, usando fallback');
      downloadUrl = `s3://${bucket}/${objectKey}`;
    }
    return { objectKey, downloadUrl };
  },

  async uploadExamImage(params: {
    farmId: string;
    animalName: string; // can be tag or name
    file: File | Blob;
    contentType?: string; // defaults to file.type or 'image/jpeg'
    bucket?: string; // defaults to 'exames'
  }): Promise<UploadBiometricResult> {
    const envBucket = (import.meta as any)?.env?.VITE_S3_BUCKET_EXAMES;
    const bucket = params.bucket || envBucket || 'exames';
    if (!envBucket && !params.bucket) {
      console.warn('VITE_S3_BUCKET_EXAMES não definido; usando fallback "exames"');
    }
    const rawContentType = params.contentType || (params.file as any)?.type || 'image/jpeg';
    const contentType = normalizeContentType(rawContentType);
    const ext = extensionFromContentType(contentType);
    const timestamp = Date.now();
    const safeAnimal = (params.animalName || 'animal')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9\-_.]/g, '');
    const objectKey = `mastite/${params.farmId}/${safeAnimal}-${timestamp}.${ext}`;

    const form = new FormData();
    form.append('objectKey', objectKey);
    form.append('contentType', contentType);
    form.append('bucket', bucket);
    form.append('file', params.file, `${safeAnimal}-${timestamp}.${ext}`);

    await apiClientBff.postFormData(`/storage/upload`, form);

    return { 
      objectKey, 
      downloadUrl: `s3://${bucket}/${objectKey}` // Exames não precisam de URL pública imediata
    };
  },

  async getTempAccess(params: {
    bucket: string;
    objectKey: string;
    request?: TempAccessRequest;
  }): Promise<TempAccessResponse> {
    const body = {
      expiresInMinutes: params.request?.expiresInMinutes ?? 300,
      maxAccess: params.request?.maxAccess ?? 100,
      description: params.request?.description ?? 'Preview',
      bucket: params.bucket,
      objectKey: params.objectKey
    };
    
    // Use BFF dedicated endpoint
    return await apiClientBff.post<TempAccessResponse>('/storage/temp-access', body);
  }
};
